<template>
  <div v-if="text" :class="textClass">
    <div v-if="!expanded" class="relative">
      <p
        ref="textRef"
        class="m-0 whitespace-pre-line line-clamp-3"
      >
        {{ text }}
      </p>
      <button
        v-if="isTruncatable"
        type="button"
        class="absolute bottom-0 right-0 bg-white py-0 pl-1 underline decoration-solid"
        @click="expanded = true"
      >
        {{ t('common.show_more') }}
      </button>
    </div>
    <p v-else class="m-0 whitespace-pre-line">
      {{ text }}<button
        type="button"
        class="inline underline decoration-solid"
        @click="expanded = false"
      > {{ t('common.show_less') }}</button>
    </p>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useTranslations } from '@/utils/translations.js';

const { t } = useTranslations();

const props = defineProps({
  text: {
    type: String,
    default: '',
  },
  textClass: {
    type: String,
    default: 'text-base leading-6 text-neutral-900',
  },
});

const textRef = ref(null);
const expanded = ref(false);
const isTruncatable = ref(false);

let resizeObserver = null;

function measureTruncation() {
  const el = textRef.value;
  if (!el || !props.text || expanded.value) {
    isTruncatable.value = false;
    return;
  }

  isTruncatable.value = el.scrollHeight > el.clientHeight + 1;
}

async function remeasure() {
  expanded.value = false;
  await nextTick();
  measureTruncation();
}

onMounted(() => {
  remeasure();
  if (textRef.value && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => measureTruncation());
    resizeObserver.observe(textRef.value);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});

watch(() => props.text, () => remeasure());
watch(expanded, async (isExpanded) => {
  if (!isExpanded) {
    await nextTick();
    measureTruncation();
  }
});
</script>
